import React from 'react';
import PropTypes from 'prop-types';
import { format, parseISO, isSameISOWeek } from 'date-fns';
import ruLocale from 'date-fns/locale/ru';

import { getEntityUrl } from '@culture/common-helpers/cultureUtils';
import { useSelector } from 'react-redux';
import { getEventNearestSeancePlace } from '@culture/common-helpers/events';

import { appSelectors } from 'ducks/app';
import { compareTwoDatesEqual } from 'utils/helpers/dates';
import BaseCard from '../BaseCard';

const dateFormat = 'd MMMM yyyy';
const formatRuIsoDate = (date) =>
	format(parseISO(date), dateFormat, {
		locale: ruLocale
	});

const getBadgeType = (seanceStartDate, seanceEndDate) => {
	const isPremiere = compareTwoDatesEqual(seanceStartDate, new Date());
	if (isPremiere) {
		return 'premiere';
	}
	const isLastWeek = isSameISOWeek(new Date(seanceEndDate), new Date());
	if (isLastWeek) {
		return 'lastWeek';
	}
	return null;
};

const EventCard = ({ event }) => {
	const {
		_id,
		title,
		name,
		thumbnailFile,
		seanceStartDate,
		seanceEndDate,
		organization,
		type,
		ageRestriction,
		places
	} = event;

	const addressPlace = getEventNearestSeancePlace(event) || places[0];

	const nearestPlaceName =
		addressPlace.institute?.title ||
		addressPlace.title ||
		addressPlace.address ||
		addressPlace.locale?.title ||
		null;

	const formattedSeansesInterval = `${formatRuIsoDate(
		seanceStartDate
	)} - ${formatRuIsoDate(seanceEndDate)}`;

	const redirectUrl = useSelector(appSelectors.selectRedirectUrl);

	const entityUrl = `${redirectUrl}${getEntityUrl('events', {
		_id,
		name,
		type
	})}`;

	const badgeType = getBadgeType(seanceStartDate, seanceEndDate);

	return (
		<BaseCard
			title={title}
			date={formattedSeansesInterval}
			organization={organization?.name}
			place={nearestPlaceName}
			thumbnailFile={thumbnailFile}
			thumbnailParams={{ width: 353, height: 265 }}
			url={entityUrl}
			ageRestriction={ageRestriction}
			badgeType={badgeType}
			placeholderIcon="logoPlaceholder"
		/>
	);
};

EventCard.propTypes = {
	event: PropTypes.shape({
		_id: PropTypes.number.isRequired,
		title: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		seanceStartDate: PropTypes.string,
		seanceEndDate: PropTypes.string,
		organization: PropTypes.shape({ name: PropTypes.string.isRequired }),
		label: PropTypes.string,
		thumbnailFile: PropTypes.object,
		type: PropTypes.string,
		ageRestriction: PropTypes.number,
		places: PropTypes.arrayOf(
			PropTypes.shape({
				title: PropTypes.string,
				address: PropTypes.string,
				institute: PropTypes.shape({
					title: PropTypes.string
				}),
				locale: PropTypes.shape({
					title: PropTypes.string
				}),
				seances: PropTypes.arrayOf(
					PropTypes.shape({
						startDate: PropTypes.string
					})
				)
			})
		).isRequired
	}).isRequired
};

export default EventCard;
